
import React from 'react';
import PageLayout from '@/components/layout/PageLayout';
import PageHero from '@/components/ui/PageHero';
import FAQSection from '@/components/ui/FAQSection';
import PricingSection from '@/components/ui/PricingSection';
import PricingCard from '@/components/ui/PricingCard';
import { BarChart3, Instagram, Users, PenTool, Camera, Mail, FileText, Package, Video } from 'lucide-react';
import ScrollReveal from '@/components/ui/ScrollReveal';
import ServiceCard from '@/components/ui/ServiceCard';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { Check } from 'lucide-react';

const Marketing = () => {
  const marketingServices = [
    {
      icon: <BarChart3 />,
      title: "Google Business Profile",
      description: "Optimize your Google Business Profile to improve local search visibility, manage reviews, and showcase your business information effectively.",
      badge: "Local SEO"
    },
    {
      icon: <Instagram />,
      title: "Social Media Management",
      description: "Professional management of your social media accounts including content creation, posting, and engagement to build your online presence.",
      badge: "Social Media"
    },
    {
      icon: <Camera />,
      title: "Content Creation",
      description: "Professional photo and video content creation for your social media, website, and marketing campaigns that resonates with your audience.",
      badge: "Content"
    },
    {
      icon: <Users />,
      title: "Personal Branding",
      description: "Develop and grow your personal brand with strategic content, coaching, and consultation to establish yourself as an industry authority.",
      badge: "Branding"
    },
    {
      icon: <PenTool />,
      title: "Social Media Setup",
      description: "Professional setup of your social media profiles optimized for your brand, messaging, and target audience.",
      badge: "Setup"
    },
    {
      icon: <FileText />,
      title: "Blog & Article Writing",
      description: "SEO-optimized blog posts and articles that establish your expertise, improve your search rankings, and engage your audience.",
      badge: "Content"
    }
  ];

  const faqs = [
    {
      question: "How quickly will I see results from marketing efforts?",
      answer: "Marketing results vary based on your industry, competition, and current online presence. Some clients see improvements in engagement and visibility within a few weeks, while more competitive goals like search ranking improvements may take 3-6 months. We'll establish clear expectations and KPIs during our initial consultation to measure progress effectively."
    },
    {
      question: "Do you handle paid advertising campaigns?",
      answer: "Yes, we manage paid advertising campaigns on platforms like Google Ads and Meta (Facebook, Instagram). Our services include campaign strategy, ad creation, audience targeting, budget management, and performance reporting. We'll work with you to establish campaign goals and optimize for the best possible return on investment."
    },
    {
      question: "How do you measure the success of marketing campaigns?",
      answer: "We establish clear key performance indicators (KPIs) for each campaign based on your business goals. These may include metrics like website traffic, lead generation, conversion rates, engagement rates, follower growth, or sales. We provide regular performance reports with actionable insights to continuously improve results."
    },
    {
      question: "Can you work with our in-house marketing team?",
      answer: "Absolutely! We're happy to collaborate with your in-house team, whether we're providing complementary services or filling specific skill gaps. Our flexible approach allows us to integrate with your existing workflows and systems to ensure a cohesive marketing strategy."
    },
    {
      question: "What industries do you specialize in for marketing?",
      answer: "We have experience across a wide range of industries including real estate, hospitality, food and restaurants, professional services, retail, construction, and more. Our team takes the time to understand your specific industry dynamics and audience needs to create effective marketing strategies tailored to your sector."
    }
  ];

  const socialMediaPricing = [
    {
      title: "Social Media Management",
      price: "Custom",
      description: "Tailored social media management solutions to meet your specific business needs and objectives.",
      features: [
        { text: "Strategic content planning", included: true },
        { text: "Account optimization", included: true },
        { text: "Audience growth strategies", included: true },
        { text: "Performance analytics", included: true }
      ],
      ctaText: "Contact for Quote",
      ctaLink: "/contact"
    },
    {
      title: "Pro Plan",
      price: "$300",
      description: "Basic social media posting package to maintain consistency.",
      features: [
        { text: "4 original posts per month", included: true },
        { text: "1 social channel", included: true },
        { text: "Licensed image creatives", included: true },
        { text: "Trending hashtags", included: true },
        { text: "No setup fee", included: true }
      ],
      ctaText: "Get Started",
      ctaLink: "/contact"
    },
    {
      title: "Plus Plan",
      price: "$500",
      description: "Enhanced posting package for growing businesses.",
      features: [
        { text: "16 original posts per month", included: true },
        { text: "3 social channels", included: true },
        { text: "Licensed image creatives", included: true },
        { text: "Trending hashtags", included: true },
        { text: "No setup fee", included: true }
      ],
      popular: true,
      ctaText: "Get Started",
      ctaLink: "/contact"
    },
    {
      title: "Platinum Plan",
      price: "$700",
      description: "Comprehensive daily posting across multiple platforms.",
      features: [
        { text: "28 original posts per month", included: true },
        { text: "6 social channels", included: true },
        { text: "Licensed image creatives", included: true },
        { text: "Trending hashtags", included: true },
        { text: "No setup fee", included: true }
      ],
      ctaText: "Get Started",
      ctaLink: "/contact"
    }
  ];

  const googleBusinessPricing = [
    {
      title: "Pro Plan",
      price: "$300",
      setupFee: "$200 setup fee",
      description: "Basic Google Business Profile optimization to improve local visibility.",
      features: [
        { text: "1 post per week", included: true },
        { text: "1 image optimization per week", included: true },
        { text: "1 Q&A per month", included: true },
        { text: "AI data-driven training", included: true },
        { text: "Monthly performance reports", included: true }
      ],
      ctaText: "Get Started",
      ctaLink: "/contact"
    },
    {
      title: "Plus Plan",
      price: "$400",
      setupFee: "$200 setup fee",
      description: "Enhanced Google Business Profile management for growing businesses.",
      features: [
        { text: "3 posts per week", included: true },
        { text: "3 image optimizations per week", included: true },
        { text: "1 Q&A every two weeks", included: true },
        { text: "AI custom tuning", included: true },
        { text: "Weekly work logs", included: true }
      ],
      popular: true,
      ctaText: "Get Started",
      ctaLink: "/contact"
    },
    {
      title: "Platinum Plan",
      price: "$500",
      setupFee: "$200 setup fee",
      description: "Comprehensive daily Google Business Profile management.",
      features: [
        { text: "Daily posts", included: true },
        { text: "Daily image optimizations", included: true },
        { text: "1 Q&A every week", included: true },
        { text: "AI review replies", included: true },
        { text: "Human content verification", included: true }
      ],
      ctaText: "Get Started",
      ctaLink: "/contact"
    }
  ];

  const blogWritingPricing = [
    {
      title: "Pro Plan",
      price: "$200",
      description: "Basic blog writing package for small businesses.",
      features: [
        { text: "1 blog article (500 words)", included: true },
        { text: "SEO keyword boost", included: true },
        { text: "AI enhanced topic research", included: true },
        { text: "AIGPT-4o written", included: true },
        { text: "Curated stock images", included: true },
        { text: "Human reviewed and refined", included: true }
      ],
      ctaText: "Get Started",
      ctaLink: "/contact"
    },
    {
      title: "Plus Plan",
      price: "$400",
      description: "Enhanced blog content for growing businesses.",
      features: [
        { text: "2 blog articles (1,000 words each)", included: true },
        { text: "SEO keyword boost", included: true },
        { text: "AI enhanced topic research", included: true },
        { text: "AIGPT-4o written", included: true },
        { text: "Curated stock images", included: true },
        { text: "Human reviewed and refined", included: true }
      ],
      popular: true,
      ctaText: "Get Started",
      ctaLink: "/contact"
    },
    {
      title: "Platinum Plan",
      price: "$600",
      description: "Comprehensive blog content strategy for established businesses.",
      features: [
        { text: "4 blog articles (2,000 words each)", included: true },
        { text: "SEO keyword boost", included: true },
        { text: "AI enhanced topic research", included: true },
        { text: "AIGPT-4o written", included: true },
        { text: "Curated stock images", included: true },
        { text: "Human reviewed and refined", included: true }
      ],
      ctaText: "Get Started",
      ctaLink: "/contact"
    }
  ];

  const otherServicesPricing = [
    {
      title: "Social Media Setup",
      price: "$500",
      description: "Complete setup of your business social profiles.",
      features: [
        { text: "Facebook account setup", included: true },
        { text: "Instagram setup", included: true },
        { text: "Google Business Profile setup", included: true },
        { text: "Brand consistency across platforms", included: true },
        { text: "Profile optimization", included: true }
      ],
      ctaText: "Get Started",
      ctaLink: "/contact"
    },
    {
      title: "Personal Branding",
      price: "Custom",
      description: "Coaching to create engaging reels and content for your personal brand.",
      features: [
        { text: "Script development", included: true },
        { text: "Camera angle guidance", included: true },
        { text: "Content ideas", included: true },
        { text: "Professional editing", included: true },
        { text: "Music integration", included: true }
      ],
      ctaText: "Contact for Quote",
      ctaLink: "/contact"
    },
    {
      title: "Content Creation",
      price: "Custom",
      description: "Professional photo and video content for your brand or business.",
      features: [
        { text: "Professional photography", included: true },
        { text: "Video production", included: true },
        { text: "Brand-consistent visuals", included: true },
        { text: "Optimized for multiple platforms", included: true },
        { text: "Regular content schedule", included: true }
      ],
      ctaText: "Contact for Quote",
      ctaLink: "/contact"
    }
  ];

  return (
    <PageLayout 
      title="Digital Marketing | Astrem" 
      description="Comprehensive marketing strategies including social media management, content creation, and paid advertising campaigns."
      ogImage="public/lovable-uploads/1515b9b5-1316-4ad0-bc10-ce5100ccf234.png"
    >
      <PageHero 
        title="Digital Marketing Services" 
        subtitle="Strategic marketing solutions that drive growth" 
        description="Our digital marketing services help businesses connect with their target audience, build their brand, and drive measurable results through strategic campaigns and compelling content."
        badge="Marketing Solutions"
        backgroundImage="https://images.unsplash.com/photo-1432888622747-4eb9a8efeb07?q=80&w=2674&auto=format&fit=crop&ixlib=rb-4.0.3"
        ctaLink="/contact"
      />
      
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <h2 className="text-3xl md:text-4xl font-display font-medium text-center mb-4">
              Our Marketing Services
            </h2>
          </ScrollReveal>
          
          <ScrollReveal delay={100}>
            <p className="text-lg text-astrem-gray-600 text-center max-w-2xl mx-auto mb-12">
              We provide a comprehensive range of marketing services to help your business stand out and connect with your target audience.
            </p>
          </ScrollReveal>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {marketingServices.map((service, index) => (
              <ScrollReveal key={index} delay={200 + (index * 50)}>
                <ServiceCard
                  icon={service.icon}
                  title={service.title}
                  description={service.description}
                  badge={service.badge}
                  href="/contact"
                />
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>
      
      <section id="google-business-profile" className="py-20 bg-astrem-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <ScrollReveal delay={200}>
              <img 
                src="https://images.unsplash.com/photo-1573804633927-bfcbcd909acd?q=80&w=2027&auto=format&fit=crop&ixlib=rb-4.0.3" 
                alt="Google Business Profile optimization" 
                className="rounded-xl shadow-lg w-full" 
              />
            </ScrollReveal>
            
            <ScrollReveal>
              <div>
                <span className="inline-block px-3 py-1 bg-astrem-gray-200 text-astrem-gray-800 rounded-full text-sm font-medium mb-4">
                  Local SEO
                </span>
                <h2 className="text-3xl md:text-4xl font-display font-medium mb-6">
                  Google Business Profile Optimization
                </h2>
                <p className="text-lg text-astrem-gray-600 mb-6">
                  Maximize your local search visibility and attract more customers with a fully optimized Google Business Profile managed by our team of experts.
                </p>
                <ul className="space-y-3 mb-8">
                  <li className="flex items-start">
                    <div className="mr-3 mt-1 bg-green-100 text-green-600 rounded-full p-1">
                      <Check className="h-4 w-4" />
                    </div>
                    <span className="text-astrem-gray-700">
                      Complete profile optimization
                    </span>
                  </li>
                  <li className="flex items-start">
                    <div className="mr-3 mt-1 bg-green-100 text-green-600 rounded-full p-1">
                      <Check className="h-4 w-4" />
                    </div>
                    <span className="text-astrem-gray-700">
                      Professional photo uploads
                    </span>
                  </li>
                  <li className="flex items-start">
                    <div className="mr-3 mt-1 bg-green-100 text-green-600 rounded-full p-1">
                      <Check className="h-4 w-4" />
                    </div>
                    <span className="text-astrem-gray-700">
                      Review management and response
                    </span>
                  </li>
                  <li className="flex items-start">
                    <div className="mr-3 mt-1 bg-green-100 text-green-600 rounded-full p-1">
                      <Check className="h-4 w-4" />
                    </div>
                    <span className="text-astrem-gray-700">
                      Review generation strategies
                    </span>
                  </li>
                </ul>
                <Button asChild className="bg-astrem-black hover:bg-astrem-gray-800 text-white rounded-full">
                  <Link to="/contact">Optimize Your Google Business Profile</Link>
                </Button>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>
      
      <section id="social-media-management" className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <ScrollReveal>
              <div>
                <span className="inline-block px-3 py-1 bg-astrem-gray-100 text-astrem-gray-800 rounded-full text-sm font-medium mb-4">
                  Social Media
                </span>
                <h2 className="text-3xl md:text-4xl font-display font-medium mb-6">
                  Social Media Management
                </h2>
                <p className="text-lg text-astrem-gray-600 mb-6">
                  Build a strong social media presence with our comprehensive management services for Instagram, Facebook, and other platforms.
                </p>
                <ul className="space-y-3 mb-8">
                  <li className="flex items-start">
                    <div className="mr-3 mt-1 bg-green-100 text-green-600 rounded-full p-1">
                      <Check className="h-4 w-4" />
                    </div>
                    <span className="text-astrem-gray-700">
                      Content creation and curation
                    </span>
                  </li>
                  <li className="flex items-start">
                    <div className="mr-3 mt-1 bg-green-100 text-green-600 rounded-full p-1">
                      <Check className="h-4 w-4" />
                    </div>
                    <span className="text-astrem-gray-700">
                      Reels, stories, and carousel posts
                    </span>
                  </li>
                  <li className="flex items-start">
                    <div className="mr-3 mt-1 bg-green-100 text-green-600 rounded-full p-1">
                      <Check className="h-4 w-4" />
                    </div>
                    <span className="text-astrem-gray-700">
                      Community engagement
                    </span>
                  </li>
                  <li className="flex items-start">
                    <div className="mr-3 mt-1 bg-green-100 text-green-600 rounded-full p-1">
                      <Check className="h-4 w-4" />
                    </div>
                    <span className="text-astrem-gray-700">
                      Performance analytics and reporting
                    </span>
                  </li>
                </ul>
                <Button asChild className="bg-astrem-black hover:bg-astrem-gray-800 text-white rounded-full">
                  <Link to="/contact">Get Started with Social Media Management</Link>
                </Button>
              </div>
            </ScrollReveal>
            
            <ScrollReveal delay={200}>
              <img 
                src="https://images.unsplash.com/photo-1611162617213-7d7a39e9b1d7?q=80&w=2574&auto=format&fit=crop&ixlib=rb-4.0.3" 
                alt="Social media management" 
                className="rounded-xl shadow-lg w-full" 
              />
            </ScrollReveal>
          </div>
        </div>
      </section>
      
      <section id="content-creation" className="py-20 bg-astrem-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <ScrollReveal delay={200}>
              <img 
                src="https://portal.astrem.co/users/u9/9466/astremagency/lovableagentsite/Person_using_laptop_taking_notes.jpg" 
                alt="Content creation" 
                className="rounded-xl shadow-lg w-full" 
              />
            </ScrollReveal>
            
            <ScrollReveal>
              <div>
                <span className="inline-block px-3 py-1 bg-astrem-gray-200 text-astrem-gray-800 rounded-full text-sm font-medium mb-4">
                  Content
                </span>
                <h2 className="text-3xl md:text-4xl font-display font-medium mb-6">
                  Content Creation
                </h2>
                <p className="text-lg text-astrem-gray-600 mb-6">
                  Engage your audience with high-quality, professionally created content that showcases your brand and drives engagement on social media.
                </p>
                <ul className="space-y-3 mb-8">
                  <li className="flex items-start">
                    <div className="mr-3 mt-1 bg-green-100 text-green-600 rounded-full p-1">
                      <Check className="h-4 w-4" />
                    </div>
                    <span className="text-astrem-gray-700">
                      Lifestyle photos and videos
                    </span>
                  </li>
                  <li className="flex items-start">
                    <div className="mr-3 mt-1 bg-green-100 text-green-600 rounded-full p-1">
                      <Check className="h-4 w-4" />
                    </div>
                    <span className="text-astrem-gray-700">
                      Engaging videos with hooks and CTAs
                    </span>
                  </li>
                  <li className="flex items-start">
                    <div className="mr-3 mt-1 bg-green-100 text-green-600 rounded-full p-1">
                      <Check className="h-4 w-4" />
                    </div>
                    <span className="text-astrem-gray-700">
                      Problem-solution focused content
                    </span>
                  </li>
                  <li className="flex items-start">
                    <div className="mr-3 mt-1 bg-green-100 text-green-600 rounded-full p-1">
                      <Check className="h-4 w-4" />
                    </div>
                    <span className="text-astrem-gray-700">
                      Brand-consistent visual assets
                    </span>
                  </li>
                </ul>
                <Button asChild className="bg-astrem-black hover:bg-astrem-gray-800 text-white rounded-full">
                  <Link to="/contact">Elevate Your Content Strategy</Link>
                </Button>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>
      
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <h2 className="text-3xl md:text-4xl font-display font-medium text-center mb-4">
              Social Media Management Packages
            </h2>
          </ScrollReveal>
          
          <ScrollReveal delay={100}>
            <p className="text-lg text-astrem-gray-600 text-center max-w-2xl mx-auto mb-12">
              Choose from our social media management packages to enhance your online presence and engage with your audience effectively.
            </p>
          </ScrollReveal>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {socialMediaPricing.slice(1).map((card, index) => (
              <ScrollReveal key={index} delay={200 + (index * 100)}>
                <PricingCard {...card} />
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>
      
      <section className="py-20 bg-astrem-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <h2 className="text-3xl md:text-4xl font-display font-medium text-center mb-4">
              Google Business Profile Ranking Packages
            </h2>
          </ScrollReveal>
          
          <ScrollReveal delay={100}>
            <p className="text-lg text-astrem-gray-600 text-center max-w-2xl mx-auto mb-12">
              Improve your local search visibility and connect with more customers through our Google Business Profile management services.
            </p>
          </ScrollReveal>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {googleBusinessPricing.map((card, index) => (
              <ScrollReveal key={index} delay={200 + (index * 100)}>
                <PricingCard {...card} />
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>
      
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <h2 className="text-3xl md:text-4xl font-display font-medium text-center mb-4">
              Blog Article Writing Packages
            </h2>
          </ScrollReveal>
          
          <ScrollReveal delay={100}>
            <p className="text-lg text-astrem-gray-600 text-center max-w-2xl mx-auto mb-12">
              Enhance your content marketing strategy with expertly written, SEO-optimized blog articles tailored to your industry.
            </p>
          </ScrollReveal>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {blogWritingPricing.map((card, index) => (
              <ScrollReveal key={index} delay={200 + (index * 100)}>
                <PricingCard {...card} />
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>
      
      <section className="py-20 bg-astrem-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <h2 className="text-3xl md:text-4xl font-display font-medium text-center mb-4">
              Other Marketing Services
            </h2>
          </ScrollReveal>
          
          <ScrollReveal delay={100}>
            <p className="text-lg text-astrem-gray-600 text-center max-w-2xl mx-auto mb-12">
              Additional marketing services to help your business stand out in the competitive digital landscape.
            </p>
          </ScrollReveal>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {otherServicesPricing.map((card, index) => (
              <ScrollReveal key={index} delay={200 + (index * 100)}>
                <PricingCard {...card} />
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>
      
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 rounded-2xl overflow-hidden">
            <div className="p-10 md:p-16 text-white">
              <ScrollReveal>
                <h2 className="text-3xl md:text-4xl font-display font-medium mb-6">
                  Bundle and Save
                </h2>
              </ScrollReveal>
              
              <ScrollReveal delay={100}>
                <p className="text-lg mb-8 max-w-2xl">
                  Get more value with our bundled packages. We offer combined solutions for website development, social media management, and Google Business Profile optimization at special rates.
                </p>
              </ScrollReveal>
              
              <ScrollReveal delay={200}>
                <ul className="space-y-4 mb-10">
                  <li className="flex items-start">
                    <div className="mr-3 mt-1 bg-white/20 text-white rounded-full p-1">
                      <Package className="h-4 w-4" />
                    </div>
                    <span className="text-white">
                      10-page website + monthly social media posts + Google Business Profile management
                    </span>
                  </li>
                  <li className="flex items-start">
                    <div className="mr-3 mt-1 bg-white/20 text-white rounded-full p-1">
                      <Package className="h-4 w-4" />
                    </div>
                    <span className="text-white">
                      Custom packages available to meet your specific business needs
                    </span>
                  </li>
                </ul>
              </ScrollReveal>
              
              <ScrollReveal delay={300}>
                <Button asChild size="lg" className="bg-white text-purple-600 hover:bg-white/90 rounded-full">
                  <Link to="/contact">
                    Get a Custom Bundle Quote
                  </Link>
                </Button>
              </ScrollReveal>
            </div>
          </div>
        </div>
      </section>
      
      <section className="py-20 bg-astrem-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <ScrollReveal>
              <div>
                <span className="inline-block px-3 py-1 bg-astrem-gray-200 text-astrem-gray-800 rounded-full text-sm font-medium mb-4">
                  Personal Branding
                </span>
                <h2 className="text-3xl md:text-4xl font-display font-medium mb-6">
                  Reels & Video Content Coaching
                </h2>
                <p className="text-lg text-astrem-gray-600 mb-6">
                  Stand out on social media with engaging reels and video content that showcases your expertise and connects with your audience.
                </p>
                <ul className="space-y-3 mb-8">
                  <li className="flex items-start">
                    <div className="mr-3 mt-1 bg-green-100 text-green-600 rounded-full p-1">
                      <Check className="h-4 w-4" />
                    </div>
                    <span className="text-astrem-gray-700">
                      Script development and content planning
                    </span>
                  </li>
                  <li className="flex items-start">
                    <div className="mr-3 mt-1 bg-green-100 text-green-600 rounded-full p-1">
                      <Check className="h-4 w-4" />
                    </div>
                    <span className="text-astrem-gray-700">
                      Camera angle and lighting guidance
                    </span>
                  </li>
                  <li className="flex items-start">
                    <div className="mr-3 mt-1 bg-green-100 text-green-600 rounded-full p-1">
                      <Check className="h-4 w-4" />
                    </div>
                    <span className="text-astrem-gray-700">
                      Professional editing and music selection
                    </span>
                  </li>
                  <li className="flex items-start">
                    <div className="mr-3 mt-1 bg-green-100 text-green-600 rounded-full p-1">
                      <Check className="h-4 w-4" />
                    </div>
                    <span className="text-astrem-gray-700">
                      Trending content strategy implementation
                    </span>
                  </li>
                </ul>
                <Button asChild className="bg-astrem-black hover:bg-astrem-gray-800 text-white rounded-full">
                  <Link to="/contact">Schedule a Coaching Session</Link>
                </Button>
              </div>
            </ScrollReveal>
            
            <ScrollReveal delay={200}>
              <img 
                src="https://portal.astrem.co/users/u9/9466/astremagency/lovableagentsite/Man_writing_at_outdoor_table.jpg" 
                alt="Man writing at outdoor table for content creation" 
                className="rounded-xl shadow-lg w-full" 
              />
            </ScrollReveal>
          </div>
        </div>
      </section>
      
      <section className="py-20 bg-astrem-black text-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <ScrollReveal>
            <h2 className="text-3xl md:text-4xl font-display font-medium mb-6">
              Ready to Transform Your Marketing?
            </h2>
          </ScrollReveal>
          
          <ScrollReveal delay={100}>
            <p className="text-lg text-white/80 max-w-2xl mx-auto mb-8">
              Let's discuss how our marketing services can help you connect with your audience, build your brand, and grow your business.
            </p>
          </ScrollReveal>
          
          <ScrollReveal delay={200}>
            <Button asChild size="lg" className="bg-white text-astrem-black hover:bg-astrem-gray-100 rounded-full">
              <Link to="/contact">Start Your Marketing Journey</Link>
            </Button>
          </ScrollReveal>
        </div>
      </section>
      
      <FAQSection faqs={faqs} />
    </PageLayout>
  );
};

export default Marketing;
